/**
 * Formats a date range display text based on the selected range type and custom dates
 * @param {string} dateRange - The selected date range ('6months', '12months', 'custom')
 * @param {string} customStartDate - Custom start date (YYYY-MM-DD format)
 * @param {string} customEndDate - Custom end date (YYYY-MM-DD format)
 * @param {Object} fallbackData - Optional fallback data object with dateRange.startDate and dateRange.endDate
 * @returns {string} Formatted date range text
 */
export const getDateRangeDisplayText = (dateRange, customStartDate, customEndDate, fallbackData = null) => {
    // Handle custom date range
    if (dateRange === 'custom' && customStartDate && customEndDate) {
        return `${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()}`;
    }
    // Handle fallback data (for components that receive API data with date ranges)
    if (fallbackData?.dateRange) {
        return `${new Date(fallbackData.dateRange.startDate).toLocaleDateString()} - ${new Date(fallbackData.dateRange.endDate).toLocaleDateString()}`;
    }
    // Handle preset ranges by calculating the actual dates
    const now = new Date();
    let startDate, endDate;
    switch (dateRange) {
        case '6months':
        default:
            startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
            endDate = now;
            break;
        case '12months':
            startDate = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
            endDate = now;
            break;
    }
    return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
};

/**
 * Gets the current date range label for display
 * @param {string} dateRange - The selected date range ('6months', '12months', 'custom')
 * @param {string} customStartDate - Custom start date (YYYY-MM-DD format)
 * @param {string} customEndDate - Custom end date (YYYY-MM-DD format)
 * @returns {string} Date range label
 */
export const getDateRangeLabel = (dateRange, customStartDate, customEndDate) => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
        return `Custom: ${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()}`;
    }
    return dateRange === '12months' ? 'Past 12 Months' : 'Past 6 Months';
};

/**
 * Calculates the actual start and end dates based on the selected range
 * @param {string} dateRange - The selected date range ('6months', '12months', 'custom')
 * @param {string} customStartDate - Custom start date (YYYY-MM-DD format)
 * @param {string} customEndDate - Custom end date (YYYY-MM-DD format)
 * @returns {Object} Object with startDate and endDate as Date objects
 */
export const calculateDateRange = (dateRange, customStartDate, customEndDate) => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
        case '6months':
        default:
            startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
            // Set start date to beginning of day
            startDate.setHours(0, 0, 0, 0);
            // Set end date to end of current day
            endDate = new Date(now);
            endDate.setHours(23, 59, 59, 999);
            break;

        case '12months':
            startDate = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
            // Set start date to beginning of day
            startDate.setHours(0, 0, 0, 0);
            // Set end date to end of current day
            endDate = new Date(now);
            endDate.setHours(23, 59, 59, 999);
            break;

        case 'custom':
            // Use custom dates if provided, otherwise fall back to 6 months
            if (customStartDate && customEndDate) {
                startDate = new Date(customStartDate);
                endDate = new Date(customEndDate);
                // Set start date to beginning of day
                startDate.setHours(0, 0, 0, 0);
                // Set end date to end of day
                endDate.setHours(23, 59, 59, 999);
            } else {
                startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date(now);
                endDate.setHours(23, 59, 59, 999);
            }
            break;
    }

    return { startDate, endDate };
};

/**
 * Formats a Date as YYYY-MM-DD using its local calendar date (not UTC like toISOString)
 * @param {Date} date - The date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const toLocalDateString = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};